import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Select } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getPaymentMethodFieldsByType,
  getPaymentMethodTypesByCountry,
  PaymentMethod,
  PaymentMethodField,
  PaymentMethodType,
  PaymentMethodTypeLabels,
} from '@/model/General/PaymentMethod';

// Labels legibles para los campos dinámicos del formulario
const fieldLabels: Record<PaymentMethodField, string> = {
  banco: 'Banco',
  nombre_cuenta: 'Nombre de cuenta',
  numero_cuenta: 'Número de cuenta',
  numero_de_agencia: 'Número de agencia',
  cbu: 'CBU',
  alias_cbu: 'Alias CBU',
  cuit_cuil: 'CUIT/CUIL',
  clave: 'Clave',
  email_pago: 'Email de pago',
};

interface PaymentMethodCreateModalProps {
  isBrasil: boolean;
  onCreatePaymentMethod?: (data: Partial<PaymentMethod>) => void;
}

/**
 * Botón + Modal para crear un nuevo método de pago.
 * Renderiza campos dinámicos según el tipo de pago seleccionado.
 */
const PaymentMethodCreateModal = ({
  isBrasil,
  onCreatePaymentMethod,
}: PaymentMethodCreateModalProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const selectedPaymentType = Form.useWatch('tipo', form);
  const paymentMethodTypeOptions = useMemo(
    () =>
      getPaymentMethodTypesByCountry(isBrasil).map(value => ({
        value,
        label: t(PaymentMethodTypeLabels[value]),
      })),
    [isBrasil, t],
  );
  const selectedPaymentFields = useMemo(
    () =>
      selectedPaymentType !== undefined
        ? getPaymentMethodFieldsByType(selectedPaymentType as PaymentMethodType, isBrasil)
        : [],
    [isBrasil, selectedPaymentType],
  );
  const getFieldLabel = (field: PaymentMethodField) =>
    field === 'cuit_cuil' && isBrasil ? 'CPF/CNPJ' : fieldLabels[field];
  const handleOk = () => {
    void form.validateFields().then(values => {
      onCreatePaymentMethod?.(values);
      setOpen(false);
      form.resetFields();
    });
  };

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
  };

  return (
    <>
      <Button icon={<PlusOutlined />} onClick={() => setOpen(true)} />
      <Modal
        title={t('Nuevo método de pago')}
        open={open}
        onCancel={handleCancel}
        onOk={handleOk}
        okText={t('Guardar')}
        cancelText={t('Cancelar')}
        destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item
            name="tipo"
            label={t('Tipo de pago')}
            rules={[{ required: true, message: t('Seleccione un tipo de pago') }]}>
            <Select options={paymentMethodTypeOptions} placeholder={t('Seleccione un tipo')} />
          </Form.Item>
          <Form.Item
            name="descripcion"
            label={t('Descripción / Referencia')}
            rules={[
              {
                required: true,
                message: t('La {{field}} es requerida', { field: t('Descripción / Referencia') }),
              },
            ]}>
            <Input />
          </Form.Item>
          {selectedPaymentFields.map(field => (
            <Form.Item key={field} name={field} label={t(getFieldLabel(field))}>
              <Input />
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </>
  );
};

export default PaymentMethodCreateModal;
